import { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Loader2,
  Printer,
  FileSpreadsheet,
} from "lucide-react";
import {
  useGetPLReport,
  useDownloadPLReport,
} from "../../service/reportsService";
import { useGetAllBranches } from "../../service/branchService";
import Loading from "../../components/common/LoaderCommon";
import { cn } from "../../utils/cn";

const isAdmin = () =>
  (localStorage.getItem("role") || "").toLowerCase() === "admin";

const fmt = (val: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(val ?? 0);

function ProfitLossReport() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [selectedBranch, setSelectedBranch] = useState(
    isAdmin() ? "" : localStorage.getItem("branchId") || "",
  );

  const canFetch = !!fromDate && !!toDate && !!selectedBranch;

  // Branch list — only needed for admin
  const { data: branchData } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const { data, isLoading } = useGetPLReport(
    {
      fromDate,
      toDate,
      branchId: Number(selectedBranch),
      isPrint: false,
    },
    canFetch,
  );

  const { mutate: download, isPending: isDownloading } = useDownloadPLReport();

  const handlePrint = () => {
    if (!canFetch) return;
    download({
      fromDate,
      toDate,
      branchId: Number(selectedBranch),
      isPrint: true,
      isExcel: false,
    });
  };

  const handleExcel = () => {
    if (!canFetch) return;
    download({
      fromDate,
      toDate,
      branchId: Number(selectedBranch),
      isPrint: false,
      isExcel: true,
    });
  };

  const inputCls =
    "h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]";
  const labelCls = "text-sm font-medium text-[#52627C] mb-1.5 block";

  return (
    <div className="space-y-5">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* From date */}
            <div>
              <label className={labelCls}>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* To date */}
            <div>
              <label className={labelCls}>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={inputCls}
              />
            </div>

            {/* Branch — dropdown for admin, read-only for user */}
            {isAdmin() ? (
              <div>
                <label className={labelCls}>Branch</label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className={cn(inputCls, "pr-8")}
                >
                  <option value="">Select branch</option>
                  {branchData?.content.map((b) => (
                    <option key={b.branchId} value={String(b.branchId)}>
                      {b.branchName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className={labelCls}>Branch</label>
                <input
                  type="text"
                  value={
                    branchData?.content.find(
                      (b) => String(b.branchId) === selectedBranch,
                    )?.branchName || selectedBranch
                  }
                  disabled
                  className={cn(inputCls, "cursor-not-allowed opacity-60")}
                />
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              disabled={!canFetch || isDownloading}
              onClick={handleExcel}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export to Excel
            </button>
            <button
              type="button"
              disabled={!canFetch || isDownloading}
              onClick={handlePrint}
              className="h-11 rounded-[12px] bg-white border border-[#E8EEF9] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="w-4 h-4" />
              Print PDF
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {!canFetch ? (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] py-16 text-center">
          <FileText className="w-10 h-10 text-[#D0DAEE] mx-auto mb-3" />
          <p className="text-sm font-medium text-[#52627C]">
            Select a date range{isAdmin() ? " and branch" : ""} to view the
            report
          </p>
        </div>
      ) : isLoading ? (
        <Loading />
      ) : data ? (
        <>
          {/* Summary KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Net Revenue"
              value={fmt(data.netRevenue)}
              icon={<DollarSign className="w-5 h-5" />}
              color="blue"
            />
            <KpiCard
              label="Gross Profit"
              value={fmt(data.grossProfit)}
              icon={<TrendingUp className="w-5 h-5" />}
              color={data.grossProfit >= 0 ? "green" : "red"}
            />
            <KpiCard
              label="Total Expenses"
              value={fmt(data.totalExpenses)}
              icon={<TrendingDown className="w-5 h-5" />}
              color="orange"
            />
            <KpiCard
              label="Net Profit"
              value={fmt(data.netProfit)}
              icon={<TrendingUp className="w-5 h-5" />}
              color={data.netProfit >= 0 ? "green" : "red"}
              large
            />
          </div>

          {/* Detailed breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Revenue section */}
            <Section title="Revenue">
              <Row label="Gross Sales" value={fmt(data.grossSalesAmount)} />
              <Row
                label="Sales Return"
                value={`(${fmt(data.salesReturnAmount)})`}
                muted
              />
              <Row
                label="Total Sales Discount"
                value={fmt(data.totalSalesDiscount)}
                muted
              />
              <Row
                label="Return Discount"
                value={fmt(data.totalReturnDiscount)}
                muted
              />
              <Divider />
              <Row label="Net Revenue" value={fmt(data.netRevenue)} bold />
              <Row label="Total Invoices" value={String(data.totalInvoices)} />
              <Row label="Total Returns" value={String(data.totalReturns)} />
            </Section>

            {/* Tax section */}
            <Section title="Tax">
              <Row label="Sales Tax" value={fmt(data.salesTax)} />
              <Row
                label="Sales Return Tax"
                value={`(${fmt(data.salesReturnTax)})`}
                muted
              />
              <Divider />
              <Row label="Net Tax" value={fmt(data.netTax)} bold />
            </Section>

            {/* Cost & Profit section */}
            <Section title="Cost & Gross Profit">
              <Row label="Net Revenue" value={fmt(data.netRevenue)} />
              <Row
                label="Cost of Goods Sold"
                value={`(${fmt(data.costOfGoodsSold)})`}
                muted
              />
              <Divider />
              <Row
                label="Gross Profit"
                value={fmt(data.grossProfit)}
                bold
                highlight={data.grossProfit >= 0 ? "green" : "red"}
              />
            </Section>

            {/* Expenses & Net Profit section */}
            <Section title="Expenses & Net Profit">
              <Row label="Cash Expenses" value={fmt(data.cashExpenses)} />
              <Row label="Bank Expenses" value={fmt(data.bankExpenses)} />
              <Divider />
              <Row
                label="Total Expenses"
                value={fmt(data.totalExpenses)}
                bold
              />
              <div className="mt-3 pt-3 border-t border-[#E8EEF9]">
                <Row
                  label="Net Profit"
                  value={fmt(data.netProfit)}
                  bold
                  large
                  highlight={data.netProfit >= 0 ? "green" : "red"}
                />
              </div>
            </Section>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

type KpiColor = "blue" | "green" | "red" | "orange";

const kpiColors: Record<KpiColor, { bg: string; icon: string; value: string }> =
  {
    blue: {
      bg: "bg-[#EEF3FF]",
      icon: "text-[#1A5EF9]",
      value: "text-[#1A5EF9]",
    },
    green: {
      bg: "bg-[#E6F9F0]",
      icon: "text-[#1A9E5C]",
      value: "text-[#1A9E5C]",
    },
    red: {
      bg: "bg-[#FFF0F0]",
      icon: "text-[#E03E3E]",
      value: "text-[#E03E3E]",
    },
    orange: {
      bg: "bg-[#FFF7ED]",
      icon: "text-[#E08A1E]",
      value: "text-[#E08A1E]",
    },
  };

function KpiCard({
  label,
  value,
  icon,
  color,
  large,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: KpiColor;
  large?: boolean;
}) {
  const c = kpiColors[color];
  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white shadow-[0_8px_24px_rgba(26,94,249,0.06)] p-5">
      <div
        className={cn(
          "w-10 h-10 rounded-[12px] flex items-center justify-center mb-3",
          c.bg,
        )}
      >
        <span className={c.icon}>{icon}</span>
      </div>
      <p className="text-xs font-medium text-[#8A9BB5] mb-1">{label}</p>
      <p className={cn("font-bold", large ? "text-2xl" : "text-lg", c.value)}>
        {value}
      </p>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
      <h3 className="text-sm font-bold text-[#1A2B4A] uppercase tracking-wide mb-4">
        {title}
      </h3>
      <div className="space-y-0">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
  large,
  highlight,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
  large?: boolean;
  highlight?: "green" | "red";
}) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-dotted border-[#EEF2FA] last:border-0">
      <span
        className={cn(
          "text-sm",
          bold ? "font-semibold text-[#1A2B4A]" : "text-[#52627C]",
          muted && "text-[#8A9BB5]",
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "font-medium tabular-nums",
          large ? "text-base font-bold" : "text-sm",
          highlight === "green" && "text-[#1A9E5C]",
          highlight === "red" && "text-[#E03E3E]",
          !highlight && (bold ? "text-[#1A2B4A]" : "text-[#43536A]"),
          muted && "text-[#8A9BB5]",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-1" />;
}

export default ProfitLossReport;
